import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, RefreshCw, UploadCloud, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { fetchSemrushClients, fetchSemrushCoverageStatus, getAuditExportUrl, uploadSemrushReport } from "../api";
import type { SemrushClientOption, SemrushCoverageItem, SemrushReportType } from "../api";
import { useDashboard } from "../context/DashboardContext";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type ExtraField = "domain" | "target" | "projectId" | "database" | "siteHealthScore";

interface ReportOption {
  value: SemrushReportType;
  label: string;
  source: string;
  fields: ExtraField[];
}

const REPORTS: ReportOption[] = [
  { value: "keywords", label: "Keywords", source: "Organic Research → Positions", fields: ["domain", "database"] },
  { value: "backlinks", label: "Backlinks", source: "Backlink Analytics → Backlinks", fields: ["target"] },
  { value: "site-audit-issues", label: "Site Audit — Issues", source: "Site Audit → Issues", fields: ["projectId", "siteHealthScore"] },
  { value: "site-audit-pages", label: "Site Audit — Pages", source: "Site Audit → Crawled Pages", fields: ["projectId"] },
  { value: "site-audit-page-issues", label: "Site Audit — Mega Export", source: "Site Audit → Páginas rastreadas → Exportar datos: Páginas rastreadas con todos los problemas", fields: ["projectId"] },
  { value: "competitors", label: "Competitors", source: "Organic Research → Competitors", fields: [] },
  { value: "keyword-gap", label: "Keyword Gap", source: "Keyword Gap (Semrush)", fields: ["domain"] },
];

export function SemrushUploadPage() {
  const { selectedClient: globalClient, setSelectedClient } = useDashboard();
  const [clients, setClients] = useState<SemrushClientOption[]>([]);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [reportType, setReportType] = useState<SemrushReportType>("keywords");
  const [domain, setDomain] = useState("");
  const [target, setTarget] = useState("");
  const [projectId, setProjectId] = useState("");
  const [database, setDatabase] = useState("cl");
  const [siteHealthScore, setSiteHealthScore] = useState("");
  const [snapshotDate, setSnapshotDate] = useState(todayIso());
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ rowsLoaded: number; clientSlug: string; domain: string; snapshotDate: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [coverage, setCoverage] = useState<SemrushCoverageItem[]>([]);
  const [loadingCoverage, setLoadingCoverage] = useState(false);

  const clientSlug = globalClient !== "ALL" ? globalClient : "";

  useEffect(() => {
    fetchSemrushClients()
      .then(setClients)
      .catch((err) => setClientsError(err instanceof Error ? err.message : String(err)));
  }, []);

  const loadCoverage = (slug: string) => {
    if (!slug) {
      setCoverage([]);
      return;
    }
    setLoadingCoverage(true);
    fetchSemrushCoverageStatus(slug)
      .then(setCoverage)
      .catch((err) => console.error("Error cargando cobertura:", err))
      .finally(() => setLoadingCoverage(false));
  };

  const report = useMemo(() => REPORTS.find((r) => r.value === reportType) ?? REPORTS[0], [reportType]);

  const selectedClient = useMemo(
    () => clients.find((client) => client.clientSlug === clientSlug),
    [clients, clientSlug],
  );

  useEffect(() => {
    if (clientSlug) {
      loadCoverage(clientSlug);
    } else {
      setCoverage([]);
    }
  }, [clientSlug]);

  useEffect(() => {
    if (selectedClient?.domain) {
      setDomain(selectedClient.domain);
      setTarget(selectedClient.domain);
    }
  }, [selectedClient]);

  function handleReportChange(value: SemrushReportType) {
    setReportType(value);
    setResult(null);
    setError(null);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!file || !clientSlug || !snapshotDate) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const fields: Record<string, string> = { client_slug: clientSlug, snapshot_date: snapshotDate };
      if (report.fields.includes("domain")) fields.domain = domain;
      if (report.fields.includes("target")) fields.target = target;
      if (report.fields.includes("projectId")) fields.project_id = projectId;
      if (report.fields.includes("database")) fields.database = database || "cl";
      if (report.fields.includes("siteHealthScore") && siteHealthScore.trim()) {
        fields.site_health_score = siteHealthScore.trim();
      }
      const response = await uploadSemrushReport(reportType, fields, file);
      setResult(response);
      loadCoverage(clientSlug);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const needsDomain = report.fields.includes("domain") && !domain;
  const needsTarget = report.fields.includes("target") && !target;
  const canSubmit = Boolean(file && clientSlug && snapshotDate) && !needsDomain && !needsTarget && !submitting;

  return (
    <div className="page">
      <section className="page-intro enter">
        <div>
          <span className="eyebrow">SEO · Semrush</span>
          <h1>Carga manual de reportes Semrush</h1>
          <p>
            Sube exports CSV de Semrush (interfaz en inglés) para un cliente. Cada reporte va a su tabla en{" "}
            <code>seo_raw</code> y se propaga automáticamente a <code>seo_core</code> y <code>seo_bi</code> vía MERGE SQL.
          </p>
        </div>
      </section>

      {!clientSlug && (
        <section className="panel" style={{ padding: "32px 20px", textAlign: "center" }}>
          <div style={{ maxWidth: "560px", margin: "0 auto" }}>
            <span className="eyebrow">Selección de cliente</span>
            <h2 style={{ fontSize: "1.2rem", margin: "8px 0" }}>Selecciona un cliente para comenzar</h2>
            <p className="panel-copy" style={{ marginBottom: "20px" }}>
              Semrush gestiona reportes por cliente. Usa el selector en la barra superior o elige uno a continuación:
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
              {clients.map((c) => (
                <button
                  key={c.clientSlug}
                  type="button"
                  className="button button-secondary"
                  onClick={() => setSelectedClient(c.clientSlug)}
                  style={{ fontSize: "13px" }}
                >
                  {c.displayName}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {clientSlug && (
        <section className="panel" style={{ marginBottom: "28px" }}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Estado del Cliente</span>
              <h2>Cobertura de reportes — {selectedClient?.displayName ?? clientSlug}</h2>
              <p className="panel-copy">
                Monitorea la frescura de los 6 reportes de Semrush y descarga la auditoría completa compilada en HTML.
              </p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                type="button"
                className="button button-secondary"
                onClick={() => loadCoverage(clientSlug)}
                disabled={loadingCoverage}
                title="Refrescar estado de cobertura"
              >
                <RefreshCw size={15} className={loadingCoverage ? "spin" : ""} />
                {loadingCoverage ? "Actualizando…" : "Actualizar"}
              </button>
              <a
                href={getAuditExportUrl(clientSlug)}
                target="_blank"
                rel="noreferrer"
                className="button button-primary"
                style={{ textDecoration: "none" }}
              >
                <Download size={15} />
                Descargar Auditoría HTML
              </a>
            </div>
          </div>

          <div style={{ padding: "0 20px 20px 20px", overflowX: "auto" }}>
            <table className="compact-table" style={{ width: "100%", textAlign: "left", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border, #2e3440)", color: "var(--muted, #8892b0)" }}>
                  <th style={{ padding: "8px 12px" }}>Reporte</th>
                  <th style={{ padding: "8px 12px" }}>Último Snapshot</th>
                  <th style={{ padding: "8px 12px" }}>Antigüedad</th>
                  <th style={{ padding: "8px 12px" }}>Filas Snapshot</th>
                  <th style={{ padding: "8px 12px" }}>Total Histórico</th>
                  <th style={{ padding: "8px 12px" }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {loadingCoverage && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: "36px 16px",
                        textAlign: "center",
                        color: "var(--muted, #8892b0)",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "13px",
                        }}
                      >
                        <RefreshCw size={16} className="spin" />
                        <span>Consultando cobertura en BigQuery...</span>
                      </span>
                    </td>
                  </tr>
                )}
                {!loadingCoverage && coverage.map((c) => {
                  let badgeClass = "badge-muted";
                  let badgeText = "Sin datos";
                  if (c.status === "FRESH") {
                    badgeClass = "badge-good";
                    badgeText = "Al día";
                  } else if (c.status === "WARNING") {
                    badgeClass = "badge-warning";
                    badgeText = "Atención (30-60d)";
                  } else if (c.status === "STALE") {
                    badgeClass = "badge-danger";
                    badgeText = "Desactualizado (>60d)";
                  }

                  return (
                    <tr key={c.reportType} style={{ borderBottom: "1px solid var(--border-soft, #1e222d)" }}>
                      <td style={{ padding: "8px 12px", fontWeight: 600 }}>{c.label}</td>
                      <td style={{ padding: "8px 12px" }}>{c.lastSnapshot ?? "—"}</td>
                      <td style={{ padding: "8px 12px" }}>{c.daysAgo !== null ? `${c.daysAgo} días` : "—"}</td>
                      <td style={{ padding: "8px 12px" }}>{c.latestRows.toLocaleString()}</td>
                      <td style={{ padding: "8px 12px" }}>{c.totalRows.toLocaleString()}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span className={`badge ${badgeClass}`}>{badgeText}</span>
                      </td>
                    </tr>
                  );
                })}
                {coverage.length === 0 && !loadingCoverage && (
                  <tr>
                    <td colSpan={6} style={{ padding: "16px 12px", textAlign: "center", color: "var(--muted, #8892b0)" }}>
                      No se encontraron registros de Semrush para este cliente.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">Upload</span>
            <h2>Nuevo snapshot</h2>
            <p className="panel-copy">
              Recarga el mismo (cliente, snapshot) las veces que quieras: cada envío reemplaza solo la versión manual
              anterior de ese reporte y ejecuta el MERGE SQL a CORE inmediatamente.
            </p>
          </div>
          <UploadCloud size={21} />
        </div>

        {clientsError && (
          <p className="text-danger" style={{ padding: "0 20px" }}>
            No se pudo cargar el catálogo de clientes: {clientsError}
          </p>
        )}

        <div className="semrush-report-tabs">
          {REPORTS.map((r) => (
            <button
              key={r.value}
              type="button"
              className={r.value === reportType ? "semrush-report-tab is-active" : "semrush-report-tab"}
              onClick={() => handleReportChange(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <p className="panel-copy" style={{ padding: "0 20px" }}>
          Exportar desde Semrush: <strong>{report.source}</strong>
        </p>

        <form onSubmit={handleSubmit} className="semrush-upload-form">
          <div className="semrush-upload-grid">
            <label className="semrush-field">
              <span>Cliente activo</span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  background: "rgba(255, 255, 255, 0.04)",
                  border: "1px solid var(--border-soft, #1e222d)",
                  borderRadius: "6px",
                  fontSize: "13px",
                  minHeight: "38px",
                  boxSizing: "border-box",
                }}
              >
                <span style={{ fontWeight: 600, color: clientSlug ? "var(--text, #fff)" : "var(--muted, #8892b0)" }}>
                  {selectedClient?.displayName ?? (clientSlug || "Ninguno seleccionado")}
                </span>
                <span style={{ fontSize: "11px", color: "var(--muted, #8892b0)" }}>
                  {clientSlug ? "Barra superior" : "Selecciona en barra superior"}
                </span>
              </div>
            </label>

            {report.fields.includes("domain") && (
              <label className="semrush-field">
                <span>{reportType === "keyword-gap" ? "Dominio propio (exacto al header del CSV)" : "Dominio consultado"}</span>
                <input
                  type="text"
                  placeholder="roda.cl"
                  value={domain}
                  onChange={(event) => setDomain(event.target.value)}
                  required
                />
                {selectedClient && !selectedClient.domain && (
                  <small className="text-warning">
                    Sin dominio Semrush mapeado en bridge_cliente_accounts — escríbelo a mano.
                  </small>
                )}
              </label>
            )}

            {report.fields.includes("target") && (
              <label className="semrush-field">
                <span>Dominio consultado (propio o competidor)</span>
                <input
                  type="text"
                  placeholder="roda.cl"
                  value={target}
                  onChange={(event) => setTarget(event.target.value)}
                  required
                />
              </label>
            )}

            {report.fields.includes("projectId") && (
              <label className="semrush-field">
                <span>Project ID Semrush (opcional)</span>
                <input
                  type="text"
                  placeholder="Opcional"
                  value={projectId}
                  onChange={(event) => setProjectId(event.target.value)}
                />
              </label>
            )}

            {report.fields.includes("database") && (
              <label className="semrush-field">
                <span>Base de datos Semrush</span>
                <input
                  type="text"
                  placeholder="cl"
                  value={database}
                  onChange={(event) => setDatabase(event.target.value)}
                />
              </label>
            )}

            {report.fields.includes("siteHealthScore") && (
              <label className="semrush-field">
                <span>Site Health % de Semrush (0-100, opcional)</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="ej. 89"
                  value={siteHealthScore}
                  onChange={(event) => setSiteHealthScore(event.target.value)}
                />
                <small className="text-muted" style={{ fontSize: "11px", marginTop: "4px" }}>
                  Puntaje global visible en Semrush (no viene en el CSV). Si lo ingresas, se reflejará directamente en la ficha del cliente.
                </small>
              </label>
            )}

            <label className="semrush-field">
              <span>Fecha del snapshot</span>
              <input
                type="date"
                value={snapshotDate}
                onChange={(event) => setSnapshotDate(event.target.value)}
                required
              />
            </label>

            <label className="semrush-field">
              <span>Archivo CSV</span>
              <input
                type="file"
                accept=".csv"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                required
              />
            </label>
          </div>

          <div className="semrush-upload-actions">
            <button type="submit" className="button button-primary" disabled={!canSubmit}>
              {submitting ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <RefreshCw size={14} className="spin" />
                  <span>Cargando a BigQuery…</span>
                </span>
              ) : (
                "Cargar a BigQuery"
              )}
            </button>
          </div>
        </form>

        {result && (
          <div className="semrush-upload-banner text-good">
            <CheckCircle2 size={18} />
            <span>
              {result.rowsLoaded} filas cargadas para <strong>{result.clientSlug}</strong>
              {result.domain ? ` (${result.domain})` : ""}, snapshot {result.snapshotDate}.
            </span>
          </div>
        )}
        {error && (
          <div className="semrush-upload-banner text-danger">
            <XCircle size={18} />
            <span>{error}</span>
          </div>
        )}
      </section>
    </div>
  );
}
